import {api} from './api';
import {storage} from './storage';
import type {User, AuthResponse, SyncEmailResponse} from '@/types';

export const authService = {
  /** Exchange a Google OAuth authorization code for a Closai auth token. */
  async signInWithGoogle(code: string): Promise<{token: string; user: User}> {
    const {data} = await api.get<AuthResponse>(`/oauth/google/login/?code=${code}`);
    storage.setToken(data.token);
    return data;
  },

  /** Link a Google account for email sync (Gmail read access). */
  async linkGoogleEmail(code: string, syncDays?: number): Promise<SyncEmailResponse> {
    const daysParam = typeof syncDays === 'number' ? `&sync_days=${syncDays}` : '';
    const {data} = await api.get<SyncEmailResponse>(
      `/oauth/google/link/?code=${code}${daysParam}`,
    );
    return data;
  },

  /** Sign in with email and password. */
  async signIn(username: string, password: string): Promise<{token: string; user: User}> {
    const {data} = await api.post<AuthResponse>('/login/', {username, password});
    storage.setToken(data.token);
    return data;
  },

  /** Create a new account. */
  async signUp(params: {
    email: string;
    first_name: string;
    last_name: string;
    password: string;
  }): Promise<{token: string; user: User}> {
    const {data} = await api.post<AuthResponse>('/signup/', {
      ...params,
      is_staff: false,
    });
    storage.setToken(data.token);
    return data;
  },

  /** Fetch the currently authenticated user. */
  async getMe(): Promise<User> {
    const {data} = await api.get<User>('/account/me/');
    return data;
  },

  /** Update the current user's profile. */
  async updateMe(userData: Partial<User>): Promise<User> {
    const {data} = await api.patch<User>('/account/me/', userData);
    return data;
  },

  /** Sign out — clear stored credentials. */
  signOut(): void {
    storage.clear();
  },
};
